import { guards } from '@shared/helpers/typeguards.helpers';
import {
  isAllowedOpenAiModel,
  type OpenAiAllowedModels,
  type ResolvedAiModelUseCase,
} from './ai-models.model';

/**
 * OpenAI Responses API `reasoning.effort` values.
 * Each model accepts a subset — see {@link OPEN_AI_REASONING_SUPPORT}.
 */
export const OPEN_AI_REASONING_EFFORTS = Object.freeze([
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
] as const);

export type OpenAiReasoningEffort = (typeof OPEN_AI_REASONING_EFFORTS)[number];

const OPEN_AI_REASONING_EFFORTS_SET: ReadonlySet<string> = new Set(
  OPEN_AI_REASONING_EFFORTS,
);

/**
 * Which `reasoning.effort` values a model accepts, and the documented default
 * when the caller omits one. Grounded in OpenAI model pages.
 */
export type OpenAiReasoningSupport = {
  efforts: readonly OpenAiReasoningEffort[];
  defaultEffort: OpenAiReasoningEffort;
};

const NONE_TO_HIGH = ['none', 'low', 'medium', 'high'] as const;
const NONE_TO_XHIGH = ['none', 'low', 'medium', 'high', 'xhigh'] as const;
const NONE_TO_MAX = ['none', 'low', 'medium', 'high', 'xhigh', 'max'] as const;
/** GPT-6 family: no `none` effort. */
const GPT_6_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/**
 * Per-model reasoning.effort support. Exhaustive over
 * {@link OpenAiAllowedModels} so adding a model without documenting effort
 * is a compile error.
 */
export const OPEN_AI_REASONING_SUPPORT = {
  'gpt-6-astra': { efforts: GPT_6_EFFORTS, defaultEffort: 'medium' },
  'gpt-6.1-sol': { efforts: GPT_6_EFFORTS, defaultEffort: 'medium' },
  'gpt-6-luna': { efforts: NONE_TO_MAX, defaultEffort: 'medium' },
  'gpt-5.6': { efforts: NONE_TO_MAX, defaultEffort: 'medium' },
  'gpt-5.5': { efforts: NONE_TO_XHIGH, defaultEffort: 'medium' },
  'gpt-5.4': { efforts: NONE_TO_XHIGH, defaultEffort: 'none' },
  'gpt-5.4-mini': { efforts: NONE_TO_XHIGH, defaultEffort: 'none' },
  'gpt-5.4-nano': { efforts: NONE_TO_XHIGH, defaultEffort: 'none' },
  'gpt-5.2': { efforts: NONE_TO_XHIGH, defaultEffort: 'none' },
  'gpt-5.1': { efforts: NONE_TO_HIGH, defaultEffort: 'none' },
} as const satisfies Record<OpenAiAllowedModels, OpenAiReasoningSupport>;

/**
 * Whether a value is any OpenAI reasoning.effort string.
 */
export const isOpenAiReasoningEffort = (
  value: unknown,
): value is OpenAiReasoningEffort => {
  return guards.is.string(value) && OPEN_AI_REASONING_EFFORTS_SET.has(value);
};

/**
 * Reasoning.effort support for an allowed OpenAI model, or undefined when
 * the model is unknown / not on the allowlist.
 */
export const getOpenAiReasoningSupport = (
  model: string,
): OpenAiReasoningSupport | undefined => {
  if (!isAllowedOpenAiModel(model)) return undefined;
  return OPEN_AI_REASONING_SUPPORT[model];
};

/**
 * Requested effort when it is valid for {@link model}; otherwise the model's
 * documented default. Undefined when the model does not take reasoning.effort.
 */
export const resolveOpenAiReasoningEffort = (
  model: string,
  requested: unknown,
): OpenAiReasoningEffort | undefined => {
  const support = getOpenAiReasoningSupport(model);
  if (!support) return undefined;
  if (
    isOpenAiReasoningEffort(requested) &&
    support.efforts.some((effort) => effort === requested)
  ) {
    return requested;
  }
  return support.defaultEffort;
};

/**
 * Effort a resolved use case sends: its configured
 * {@link AiModelUseCase.reasoningEffort}, adjusted to what the resolved model
 * accepts (an env override may pick a model without that effort). Undefined
 * when the use case configures none, so the provider default applies.
 */
export const resolveUseCaseReasoningEffort = (
  useCase: ResolvedAiModelUseCase,
): OpenAiReasoningEffort | undefined => {
  if (useCase.provider !== 'openai' || useCase.modality !== 'text') {
    return undefined;
  }
  if (useCase.reasoningEffort === undefined) return undefined;
  return resolveOpenAiReasoningEffort(
    useCase.resolvedModel,
    useCase.reasoningEffort,
  );
};
